import { Injectable } from '@angular/core';
import { Business, BusinessAddress } from '@models/businesses';
import { AllAdjustmentsCalculated } from '@state/matter/adjustments/adjustments.selectors';
import { cloneDeep } from 'lodash';
import { BaseFormMapper } from '../../base-form-mapper';
import {
	Matter,
	PersonMatterClient,
	BusinessMatterClient,
	PersonMatterClientWithAssociations,
	BusinessMatterClientWithAssociations,
	CalculatedLandTransferTax,
	ClientsCapacity,
	MatterTitle,
} from '@models/matter';
import { isSet } from '@core/util/value';
import { addressString } from '@core/util/address';
import {
	clientIsPerson,
	clientLegalName,
	isGuarantor,
} from '@core/util/client';
import { MatterMortgage } from '@models/mortgage';
import { formatCentsToDollars } from '@core/util/money';
import { NameFormattingSettings } from '@models/businessSettings/settings';
import { joinNames } from '@core/util/string';
@Injectable({
	providedIn: 'root',
})
export class RBC_RESIDENTIAL_REQUEST_FOR_FUNDS_Mapper extends BaseFormMapper<RBC_RESIDENTIAL_REQUEST_FOR_FUNDS> {
	CloserToFormMappings = Mappings;

	mergeFormData(
		formData: RBC_RESIDENTIAL_REQUEST_FOR_FUNDS,
		matter: Matter,
		nameFormattingSettings: NameFormattingSettings,
		sellers: (PersonMatterClient | BusinessMatterClient)[],
		buyers: (PersonMatterClient | BusinessMatterClient)[],
		adjustments: AllAdjustmentsCalculated,
		business: Business,
		matterTitles: MatterTitle[],
		matterClients: (
			| PersonMatterClientWithAssociations
			| BusinessMatterClientWithAssociations
		)[],
		lttCalculated: CalculatedLandTransferTax,
		businessAddress: BusinessAddress,
		otherParties: PersonMatterClient[],
		clientsCapacity: ClientsCapacity,
		mortgage: MatterMortgage,
	): RBC_RESIDENTIAL_REQUEST_FOR_FUNDS {
		const _formData = formData
			? cloneDeep(formData)
			: ({} as RBC_RESIDENTIAL_REQUEST_FOR_FUNDS);

		if (!isSet(_formData?.checkAllprovinceType)) {
			_formData.checkAllprovinceType = 'On';
		}

		if (matterClients?.length) {
			const FilteredMatterClients = matterClients.filter(
				(client) => !isGuarantor(client),
			);

			const Contacts = FilteredMatterClients.filter((v) =>
				clientIsPerson(v),
			) as PersonMatterClientWithAssociations[];
			for (let i = 0; i < Contacts.length && i < 2; i++) {
				const contact = Contacts[i];
				if (i === 0) {
					if (!isSet(_formData?.firstClientFirstName)) {
						_formData.firstClientFirstName = contact?.firstName;
					}
					if (!isSet(_formData?.firstClientLastName)) {
						_formData.firstClientLastName = contact?.lastName;
					}
				} else {
					if (!isSet(_formData?.secondClientFirstName)) {
						_formData.secondClientFirstName = contact?.firstName;
					}
					if (!isSet(_formData?.secondClientLastName)) {
						_formData.secondClientLastName = contact?.lastName;
					}
				}
			}
		}

		if (mortgage?.guarantors && !isSet(_formData?.guarantors)) {
			const FilteredMatterClients = matterClients?.filter(
				(client) =>
					isGuarantor(client) && mortgage.guarantors.includes(client.id),
			);
			_formData.guarantors = joinNames(
				FilteredMatterClients.map((client) =>
					clientLegalName(client, nameFormattingSettings, {
						applyFormatting: false,
					}),
				),
			);
		}

		if (mortgage?.referenceNumber && !isSet(_formData?.mortgageLoanNumber)) {
			_formData.mortgageLoanNumber = mortgage.referenceNumber;
		}

		if (matter?.propertyDetails?.address && !isSet(_formData?.propertyStreet)) {
			_formData.propertyStreet = matter?.propertyDetails?.address?.street;
		}

		if (matter?.propertyDetails?.address && !isSet(_formData?.propertyCity)) {
			_formData.propertyCity = matter?.propertyDetails?.address?.city;
		}

		if (!isSet(_formData?.propertyProvinceCode)) {
			const six = 6;
			_formData.propertyProvinceCode = six.toString();
		}

		if (mortgage?.amount && !isSet(_formData?.mortgageAmount)) {
			_formData.mortgageAmount = formatCentsToDollars(mortgage.amount);
		}

		if (mortgage?.registrationNumber && !isSet(_formData?.registrationNumber)) {
			_formData.registrationNumber = mortgage.registrationNumber;
		}

		if (
			matter?.propertyDetails?.lro?.selected &&
			!isSet(_formData?.landTitle)
		) {
			_formData.landTitle = matter.propertyDetails.lro.selected.name;
		}
		// lawFirmAddress:'topmostSubform[0].Page2[0].NOTAIRE_ADRESSE[0]',

		if (businessAddress?.street && !isSet(_formData?.lawFirmAddress)) {
			_formData.lawFirmAddress = addressString(businessAddress);
		}

		if (matter?.lawyerAssigned && !isSet(_formData?.lawyerName)) {
			_formData.lawyerName = matter.lawyerAssigned.name;
		}

		return _formData;
	}

	MapValues(): Partial<RBC_RESIDENTIAL_REQUEST_FOR_FUNDS> {
		return {};
	}
}

const Mappings: RBC_RESIDENTIAL_REQUEST_FOR_FUNDS = {
	// "0":"topmostSubform[0]",
	// "1":"topmostSubform[0].Page2[0]",
	// "2":"topmostSubform[0].Page2[0].MonthDayYear2[0]",
	// "3":"topmostSubform[0].Page2[0].signatureLawyerNotary[0]",
	pickupCourierName: 'topmostSubform[0].Page2[0].courrierPickUp[0]',
	draftAmount: 'topmostSubform[0].Page2[0].DRAFT_LOAN_AMT[0]',
	lawFirmAddress: 'topmostSubform[0].Page2[0].NOTAIRE_ADRESSE[0]',
	lawyerName: 'topmostSubform[0].Page2[0].NOTAIRE_NOM_FOLIO[0]',
	financialInstitutionName: 'topmostSubform[0].Page2[0].TRUST_IF[0]',
	bankNumber: 'topmostSubform[0].Page2[0].TRUST_INST[0]',
	bankTransitNumber: 'topmostSubform[0].Page2[0].TRUST_BRANCH[0]',
	trustAccount: 'topmostSubform[0].Page2[0].TRUST_ACCOUNT[0]',
	creditAmount: 'topmostSubform[0].Page2[0].DISBURSED_NO_DOLLAR_SIGN[0]',
	fundProvider: 'topmostSubform[0].Page2[0].CHK_FUNDS_TRANSFER[0]',
	page2Checkbox3: 'topmostSubform[0].Page2[0].box3[0]',
	page2Checkbox2: 'topmostSubform[0].Page2[0].box2[0]',
	page2Checkbox1: 'topmostSubform[0].Page2[0].box1[0]',
	forwardDate: 'topmostSubform[0].Page2[0].DT_DEBOURS_YYYYMMDD[0]',
	// "18":"topmostSubform[0].Page1[0]",
	checkbox3: 'topmostSubform[0].Page1[0].asSoon3[0]',
	checkbox2: 'topmostSubform[0].Page1[0].asSoon2[0]',
	checkbox1: 'topmostSubform[0].Page1[0].asSoon1[0]',
	landTitle: 'topmostSubform[0].Page1[0].DISCHARGE_LAND_REG_DIV[0]',
	registrationNumber: 'topmostSubform[0].Page1[0].DISCHARGE_REG_NUM[0]',
	mortgageAmount: 'topmostSubform[0].Page1[0].MONTANT_PUBLIE[0]',
	propertyProvinceCode: 'topmostSubform[0].Page1[0].ADR_PROP1_CODE_PROVINCE[0]',
	propertyCity: 'topmostSubform[0].Page1[0].ADR_PROP1_VILLE[0]',
	propertyStreet: 'topmostSubform[0].Page1[0].ADR_PROP1_RUE1[0]',
	creditApplicationNumber:
		'topmostSubform[0].Page1[0].CREDIT_APPLICATION_NUMBER[0]',
	mortgageLoanNumber: 'topmostSubform[0].Page1[0].MORTREFNO[0]',
	selectedLender: 'topmostSubform[0].Page1[0].CHK_SELECT_LENDER[0]',
	guarantors: 'topmostSubform[0].Page1[0].GUA_LINE[0]',
	secondClientLastName: 'topmostSubform[0].Page1[0].DEBITEUR2_NOM[0]',
	secondClientFirstName: 'topmostSubform[0].Page1[0].DEBITEUR2_PRENOM[0]',
	firstClientLastName: 'topmostSubform[0].Page1[0].BWRLASTNM_OR_COMPANYNAME[0]',
	firstClientFirstName: 'topmostSubform[0].Page1[0].DEBITEUR1_PRENOM[0]',
	checkQuebecOnlyType: 'topmostSubform[0].Page1[0].QC_NU_BOX[0]',
	checkAllprovinceType: 'topmostSubform[0].Page1[0].ROC_BOX[0]',
	// "38":"topmostSubform[0].Page1[0].BARCODE_FAX_RETURN_LENDER[0]"
};

export interface RBC_RESIDENTIAL_REQUEST_FOR_FUNDS {
	// requestType:string,
	checkAllprovinceType: string;
	checkQuebecOnlyType: string;

	firstClientFirstName: string;
	firstClientLastName: string;

	secondClientFirstName: string;
	secondClientLastName: string;

	guarantors: string;

	selectedLender: string;
	mortgageLoanNumber: string;
	creditApplicationNumber: string;

	// clientSRF:string,
	// creditApplicationNumber:string,
	// transitNumber:string,

	propertyStreet: string;
	propertyCity: string;
	propertyProvinceCode: string;

	mortgageAmount: string;
	registrationNumber: string;
	landTitle: string;

	checkbox1: string;
	checkbox2: string;
	checkbox3: string;

	forwardDate: string;

	page2Checkbox1: string;
	page2Checkbox2: string;
	page2Checkbox3: string;

	fundProvider: string;

	creditAmount: string;
	trustAccount: string;
	bankTransitNumber: string;
	bankNumber: string;

	financialInstitutionName: string;

	lawyerName: string;
	lawFirmAddress: string;

	draftAmount: string;
	pickupCourierName: string;
}
